import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetNotification } from '../../../context/Notification';
import envVars from '../../../constants/envVars';
import useRequest from '../../../hooks/useRequest';
import { LEAD_DETAILS } from './constants';

// HWM-22633 — cancel a previously-booked medical-check appointment via the
// dr-chrono scheduling endpoint.
//
// [round 4d] `is_reschedule=true` opts into the backend's reschedule mode
// (skips the auto-revert of approval_status → INITIAL_INTEREST), so a
// followup `/scheduling/book-appointment/` call can stamp the new slot
// without first dropping the lead out of MEDICAL_CHECK status. When set,
// suppress the "cancelled" toast — the reschedule flow surfaces a single
// success notification on the followup book.

export type CancelMedicalCheckPayload = {
    lead_id: string;
    is_reschedule?: boolean;
};

const useCancelMedicalCheck = (onSuccess?: () => void) => {
    const req = useRequest();
    const queryClient = useQueryClient();
    const setNotification = useSetNotification();

    const request = ({ payload }: { payload: CancelMedicalCheckPayload }) => {
        return req({
            url: `${envVars.API_RTM_HOSTNAME}scheduling/cancel-appointment/`,
            method: 'POST',
            body: JSON.stringify(payload)
        });
    };

    return useMutation({
        mutationFn: request,
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({ queryKey: [LEAD_DETAILS] });
            if (!vars.payload.is_reschedule) {
                setNotification({ message: 'Medical check cancelled' });
            }
            onSuccess && onSuccess();
        },
        onError: (error: { errors: string[] }) => {
            return error;
        }
    });
};

export default useCancelMedicalCheck;
